import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { RefreshCw, X } from "lucide-react";
import { resetAllProgress } from "@/engine/resetProgress";
import { useIsMobile } from "@/hooks/use-mobile";
import { useBodyScrollLock } from "@/hooks/useBodyScrollLock";

/**
 * Stale-cache rescue dialog.
 *
 * MANUAL-TRIGGER ONLY: this dialog never fires automatically. It is
 * mounted at the App root and opens only when something in the UI
 * dispatches the RESCUE_REOPEN_EVENT (currently the Settings panel's
 * "Display issues after an update?" button). The previous
 * auto-on-build-change behaviour was removed because it fired on
 * every Lovable deploy, which became tedious for returning players.
 *
 * Wording is honest about what the button can and cannot do:
 *   - CAN: clear the site's saved progress (localStorage / sessionStorage)
 *   - CAN: hard-reload with a cache-busting query string
 *   - CANNOT: disable the browser's HTTP cache or block cookies — only
 *     the user can do that in browser settings or DevTools. The game
 *     has no cookies to mention.
 */
export const RESCUE_REOPEN_EVENT = "coe:rescue-reopen";

export default function StaleCacheRescueDialog() {
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  // Lock body scroll while the rescue dialog is open (iOS rubber-band).
  useBodyScrollLock(open);

  // Listen for manual trigger (Settings → "Display issues after an update?").
  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener(RESCUE_REOPEN_EVENT, handler);
    return () => window.removeEventListener(RESCUE_REOPEN_EVENT, handler);
  }, []);

  const handleDismiss = () => setOpen(false);

  const handleClearAndRefresh = () => {
    resetAllProgress();
    // Cache-bust query string forces the browser to refetch the document
    // rather than serve a stale copy from its HTTP cache.
    window.location.href = "/?v=" + Date.now();
  };

  const innerContent = (
    <>
      {/* Gold top edge — matches SettingsPanel */}
      <div
        className="h-[2px]"
        style={{ background: "linear-gradient(90deg, transparent, hsl(43, 72%, 55% / 0.3), transparent)" }}
      />

      {/* Header */}
      <div className="flex items-center justify-between px-5 pt-4 pb-3">
        <h2 className="font-display text-sm text-foreground uppercase tracking-[0.2em]">
          Display issues?
        </h2>
        <button
          onClick={handleDismiss}
          aria-label="Dismiss"
          className="p-2 min-w-[44px] min-h-[44px] flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="h-px mx-5" style={{ background: "hsl(30, 10%, 22%)" }} />

      {/* Body */}
      <div className="px-5 py-5 space-y-4">
        <div className="flex justify-center">
          <div className="w-10 h-10 rounded-full border border-primary/30 bg-primary/10 flex items-center justify-center">
            <RefreshCw className="w-5 h-5 text-primary" />
          </div>
        </div>

        <p className="font-body text-xs sm:text-sm text-muted-foreground/85 leading-relaxed">
          If anything looks broken (missing scenes, stuck choices, or progress that doesn't match what
          you remember) your browser may be holding on to an older version of the game alongside your
          saved progress. Clearing saved progress and refreshing fixes most cases.
        </p>

        <p className="font-body text-[11px] sm:text-xs text-muted-foreground/60 italic leading-relaxed">
          This will erase your saved chronicles. If you only want to reset progress, use Settings → Reset
          Progress instead.
        </p>

        <div className="h-px" style={{ background: "hsl(30, 10%, 22%)" }} />

        <div className="space-y-2">
          <button
            onClick={handleClearAndRefresh}
            className="w-full min-h-[44px] sm:min-h-0 sm:py-2.5 rounded-sm font-display text-[10px] uppercase tracking-[0.15em] text-destructive-foreground bg-destructive/80 hover:bg-destructive border border-destructive transition-colors px-3"
          >
            Clear &amp; Refresh
          </button>
          <button
            onClick={handleDismiss}
            className="w-full min-h-[44px] sm:min-h-0 sm:py-2 rounded-sm font-display text-[10px] uppercase tracking-[0.15em] text-muted-foreground border border-border/40 hover:border-border/70 hover:text-foreground transition-colors px-3"
          >
            Cancel
          </button>
        </div>
      </div>

      {/* Gold bottom edge */}
      <div
        className="h-[2px]"
        style={{ background: "linear-gradient(90deg, transparent, hsl(43, 72%, 55% / 0.15), transparent)" }}
      />
    </>
  );

  return createPortal(
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[10002] bg-black/70 backdrop-blur-sm"
            onClick={handleDismiss}
          />

          {isMobile ? (
            <motion.div
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 30, stiffness: 300 }}
              className="fixed bottom-0 left-0 right-0 z-[10003] rounded-t-lg overflow-y-auto pb-safe max-h-[90vh] supports-[height:100dvh]:max-h-[90dvh]"
              style={{
                background: "var(--surface-tile)",
                border: "1px solid hsl(30, 12%, 22%)",
                boxShadow: "0 -8px 30px hsl(30, 20%, 4% / 0.5)",
              }}
            >
              <div className="flex justify-center pt-3 pb-0">
                <div className="w-10 h-1 rounded-full bg-border/60" />
              </div>
              {innerContent}
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="fixed z-[10003] inset-0 m-auto w-[420px] max-w-[calc(100vw-32px)] h-fit max-h-[calc(100vh-64px)] supports-[height:100dvh]:max-h-[calc(100dvh-64px)] overflow-y-auto rounded-sm"
              style={{
                background: "var(--surface-tile)",
                border: "1px solid hsl(30, 12%, 22%)",
                boxShadow: "0 16px 48px hsl(30, 20%, 4% / 0.7)",
              }}
            >
              {innerContent}
            </motion.div>
          )}
        </>
      )}
    </AnimatePresence>,
    document.body
  );
}
